/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import {usePostRequest} from "../../../../api/helper";
import SearchItems from "./SearchItems";
import RecentlyViewed from "./RecentlyViewed";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import Favorites from "./Favorites";
import styled from "styled-components";
import TestDrag from "./TestDrag/TestDrag";
import {DefaultSplitter, Split} from "@geoffcox/react-splitter";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import cloneDeep from 'lodash/cloneDeep'
import {baseBlue} from '../../../../constants/colors'
import ArgosPopover from "../../../components/Popper/ArgosPopover";
import {useSearchParams} from "react-router-dom";
import {Checkbox, FormControlLabel, FormGroup} from '@mui/material';
import JobExecutionStatus from "./JobExecutionStatus";
import {useAppSelector} from "../../../../redux/hooks";
import {selectCurrentUser} from "../../../../redux/ducks/AccessControl/slice";
import {useCurrentUserHasThisAccessLevelToRouteKey} from "../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EArgosRouteKeys} from "../../../../Routes/constants";
import {EUserPermissionAccess} from "../../../../redux/ducks/AccessControl/types";
import {dashboardTitle} from "../constants";

const GAP = 20
const PADDING = 20

const StyledHeader = styled.div({
  fontSize: 17,
  fontWeight: 700
})
const Col = styled.div({
  display: 'flex',
  // flexDirection: 'vertical',
  height: '100%',
  flex: 1,
  flexDirection: 'column',
  gap: GAP,
  padding: '0px 10px 0px 10px'
})
export const Card = styled.div({
  background: 'white',
  padding: PADDING,
  flex: 1,
  height: '100%',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${baseBlue}`,
  borderRadius: 10,
})

const Wrapper = styled.div({
  flex: 1, display: 'flex', flexDirection: 'column', gap: GAP,
})
const RECENTLY_VIEWED_LABEL = 'Recently Viewed'
const FIRST_COL_CARDS_KEYS: any = [
  {
    label: "Search",
    value: <SearchItems/>,
    visible: true,
    noDefaultLayout: true,
    disableDrag: true
  },
  {
    label: "Favorites",
    value: <Favorites/>,
    visible: true
  },
  {
    label: RECENTLY_VIEWED_LABEL,
    value: <RecentlyViewed/>,
    visible: true,
    cardPaddingBottom: '5px'
  }
]
const SECOND_COL_CARDS_KEYS: any = [
  // {
  //   label: "Tasks",
  //   value: <></>,
  //   visible: false,
  // },
  {
    label: "Job Execution Status",
    value: <JobExecutionStatus/>,
    visible: true,
    disableDrag: true
  }
]

const VISIBLE = (FIRST_COL_CARDS_KEYS.concat(SECOND_COL_CARDS_KEYS)).map((v: any, i: number) => {
  return {label: v.label, visible: v.visible}
})

const mapItems = (keys: any[]) => {
  let obj = keys.map((v: any, i: number) => {
    const cardPaddingBottom = v.cardPaddingBottom ?? PADDING
    const comp = v.value
    const id = i + 1
    const label = v.label
    const card = v?.noDefaultLayout ?
      <>
        <Card>
          {comp}
        </Card>
      </> :
      <Card style={{paddingBottom: cardPaddingBottom}}>
        <StyledHeader>{label}</StyledHeader>
        <div style={{overflow: 'auto', flex: 1}}>
          {comp}
        </div>
      </Card>

    return {
      ...v,
      id: id,
      value: card,
      visible: v.visible,
      label: label
    }
  })
  return obj
}


export default function LandingPage() {

  const [queryParameters] = useSearchParams()

  useEffect(() => {
    if (queryParameters.get("login")) {
      loginUpdate()
    }
    document.title = dashboardTitle;
    return () => {
      document.title = 'Argos';
    };
  }, [])
  const LoginHandle = usePostRequest("/HttpTriggerUserLoginUpdate")
  const loginUpdate = () => {
    LoginHandle().then((res) => {
    }).catch((err) => {
      // TODO: handle error
      console.error(err)
    })
  }

  return (
    <div style={{backgroundColor: 'white', height: '100%'}}>
      <HomePageLayout/>
    </div>
  )
}


const FIRST_COL_SIZE = 40
const SECOND_COL_SIZE = 100 - FIRST_COL_SIZE

function HomePageLayout() {
  const user = useAppSelector(state => selectCurrentUser(state))
  const hasCovreqAccess = useCurrentUserHasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, EUserPermissionAccess.READ_WRITE)

  const [firstColItems, setFirstColItems] = useState<any>(() => {
    return mapItems(FIRST_COL_CARDS_KEYS) ?? []
  })
  const [secondColItems, setSecondColItems] = useState<any>()
  const [vislst, setVislst] = useState<any>(VISIBLE)
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, i: any) => {
    if (i < firstColItems.length) {
      setFirstColItems((prevState: any) => {
        let arr = [...prevState]
        arr[i].visible = event.target.checked
        return arr
      })
    } else {
      setSecondColItems((prevState: any) => {
        let _i = i - firstColItems.length
        let arr = [...prevState]
        arr[_i].visible = event.target.checked
        return arr
      })
    }
    setVislst((prevState: any) => {
      let arr = cloneDeep(prevState)
      arr[i] = {...arr[i], visible: event.target.checked}
      return arr
    })
  };
  const popperItems = firstColItems.concat(secondColItems ?? [])
  const isSecondColEmpty = secondColItems?.filter((v: any) => v.visible).length === 0 ?? true
  const isFistColEmpty = firstColItems.filter((v: any) => v.visible).length === 0

  useEffect(() => {
    if(!user) return
    const filteredSecondColItems = hasCovreqAccess ? SECOND_COL_CARDS_KEYS : SECOND_COL_CARDS_KEYS.filter((item: any) => item.label !== "Job Execution Status");
    const mappedItems = mapItems(filteredSecondColItems) ?? []
    setSecondColItems(mappedItems)
  }, [hasCovreqAccess, user]);
  return (
    <ArgosFlexbox vertical style={{height: '100%', padding: 5, paddingBottom: 10, overflow: 'auto'}} rowGap={2}>
      <div style={{marginLeft: 'auto', paddingRight: 15}}>
        <ArgosPopover button={<MoreHorizIcon/>}>
          <FormGroup>
            {popperItems?.map((v: any, i: number) => {
              const checked = vislst?.[i].visible
              return (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => handleChange(e, i)}
                    />
                  }
                  label={v?.label}
                />
              )
            })}
          </FormGroup>
        </ArgosPopover>
      </div>
      <Split renderSplitter={(splitterProps) => {
        if (isSecondColEmpty || isFistColEmpty) return null
        return <DefaultSplitter pixelSize={splitterProps.pixelSize} horizontal={splitterProps.horizontal}
                                dragging={splitterProps.dragging}/>
      }} initialPrimarySize={isSecondColEmpty ? '100%' : isFistColEmpty ? '0%' : `${FIRST_COL_SIZE}%`}
             minPrimarySize={isSecondColEmpty ? '100%' : '0%'}
             minSecondarySize={isFistColEmpty ? '100%' : '0%'}
      >
        <Col>
          <Wrapper style={{overflow: 'auto'}}>
            <TestDrag items={firstColItems}/>
          </Wrapper>
        </Col>
        <Col style={{}}>
          <Wrapper style={{overflow: 'auto'}}>
            <TestDrag items={secondColItems}/>
          </Wrapper>
        </Col>
      </Split>
    </ArgosFlexbox>
  )
}



